import { DateTime } from "luxon";

import { ajaxController } from "./controller/AjaxController";
import { clientMutationOptions } from "./controller/clientMutation";
import { PagedResult } from "../DTOs/Common/PagedResult";
import { DrillingDTO } from "../DTOs/Drilling/DrillingDTO";
import { DrillingBlastHoleDTO } from "../DTOs/Drilling/DrillingBlastHoleDTO";
import { DrillingPreviewDTO } from "../DTOs/Drilling/DrillingPreviewDTO";
import { DrillingBlastPatternDTO } from "../DTOs/Drilling/DrillingBlastPatternDTO";
import { DrillingEntryMode } from "../DTOs/apiEnums.generated";
import { FlatGroupOption } from "../DTOs/Common/FlatGroupOption";
import { DrillingBreadcrumbInfo } from "../DTOs/Drilling/DrillingBreadcrumbInfo";
import { PlantDTO } from "../DTOs/Plants/PlantDTO";
import { TripleShift } from "../types/Shift";
import { GetBHDrillingSummaryPagedResultDTO } from "../DTOs/Drilling/GetBHDrillingSummaryPagedResultDTO";

class DrillingAjax {
  private _ajaxController = ajaxController;
  private _drillingDataEndpoint = "/Drilling";

  // drilling rigs
  public getAvailableRigs(shiftDate: DateTime, dayNightAfternoon: TripleShift) {
    return this._ajaxController.get<PagedResult<PlantDTO>>(
        `${this._drillingDataEndpoint}/available-rigs/${shiftDate.toISODate()}/${dayNightAfternoon[0]}`,
        {}
    );
  }

  public getDrilling(shiftDate: DateTime, dayNightAfternoon: TripleShift, rigId: number) {
    return this._ajaxController.get<DrillingDTO>(
        `${this._drillingDataEndpoint}/drilling/${shiftDate.toISODate()}/${dayNightAfternoon[0]}/${rigId}`,
        {}
    );
  }

  public getDrillingPreviews(blastPatternId: number) {
    return this._ajaxController.get<PagedResult<DrillingPreviewDTO>>(
        `${this._drillingDataEndpoint}/drilling-preview/${blastPatternId}`,
        {}
    );
  }

  public createDrilling(shiftDate: DateTime, dayNightAfternoon: TripleShift, rigId: number, clientId?: string) {
    return this._ajaxController.post<object, DrillingDTO>(
        `${this._drillingDataEndpoint}/drilling/${shiftDate.toISODate()}/${dayNightAfternoon[0]}/${rigId}`,
        {},
        clientMutationOptions(clientId)
    );
  }

  public getDrillingBreadcrumbInfo(drillingId: number) {
    return this._ajaxController.get<DrillingBreadcrumbInfo>(
        `${this._drillingDataEndpoint}/breadcrumb-info/${drillingId}`,
        {}
    );
  }

  // drilling summary
  public getBHDrillingSummary(shiftDate: DateTime, dayNightAfternoon: TripleShift, req: { pageNumber?: number; pageSize?: number; sortField?: string; sortDirection?: string; search?: string }) {
    return this._ajaxController.get<GetBHDrillingSummaryPagedResultDTO>(
        `${this._drillingDataEndpoint}/bh-drilling-summary/${shiftDate.toISODate()}/${dayNightAfternoon[0]}`,
        {
          ...req,
          search: req.search ?? undefined,
        }
    );
  }

  // blast patterns
  public getBlastPatternsForDrilling = (drillingId: number) => {
    return this._ajaxController.get<PagedResult<DrillingBlastPatternDTO>>(
        `${this._drillingDataEndpoint}/blast-patterns/${drillingId}`,
        {}
    );
  }

  public getAvailableBlastPatterns = () => {
    return this._ajaxController.get<PagedResult<FlatGroupOption>>(
        `${this._drillingDataEndpoint}/blast-patterns`,
        {}
    );
  }

  public addBlastPatternToDrilling(drillingId: number, blastPatternId: number, clientId?: string, entryMode = DrillingEntryMode.Summary) {
    return this._ajaxController.post<null, DrillingBlastPatternDTO>(
        `${this._drillingDataEndpoint}/blast-patterns/${drillingId}/${blastPatternId}?entryMode=${entryMode}`,
        null,
        clientMutationOptions(clientId)
    );
  }

  public removeBlastDrilling(drillingBlastId: number, clientId?: string) {
    return this._ajaxController.delete(
        `${this._drillingDataEndpoint}/blast-patterns/${drillingBlastId}`,
        clientMutationOptions(clientId)
    );
  }

  // drilling blast holes
  public getDrillingBlastHoles(drillingBlastId: number) {
    return this._ajaxController.get<PagedResult<DrillingBlastHoleDTO>>(
        `${this._drillingDataEndpoint}/blast-holes/${drillingBlastId}`,
        {}
    );
  }

  public addDrillingBlastHole(newDrillingBlastHole: DrillingBlastHoleDTO, clientId?: string) {
    return this._ajaxController.post<DrillingBlastHoleDTO, DrillingBlastHoleDTO>(
        `${this._drillingDataEndpoint}/blast-holes`,
        newDrillingBlastHole,
        clientMutationOptions(clientId)
    );
  }

  public updateDrillingBlastHole(updatedDrillingBlastHole: DrillingBlastHoleDTO, clientId?: string) {
    return this._ajaxController.put<DrillingBlastHoleDTO>(
        `${this._drillingDataEndpoint}/blast-holes`,
        updatedDrillingBlastHole,
        clientMutationOptions(clientId)
    );
  }

  public deleteDrillingBlastHole(drillingBlastHoleId: number, clientId?: string) {
    return this._ajaxController.delete(
        `${this._drillingDataEndpoint}/blast-holes/${drillingBlastHoleId}`,
        clientMutationOptions(clientId)
    );
  }
}

export const drillingAjax = new DrillingAjax();